import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import Tippy from '@tippyjs/react';

import { requestApi } from 'api/Api';

import { arrayToObject, ia, iaRa, mString } from 'lib/helpers/utility';

import { amountToCents } from 'components/practice/BeyondBilling/Claims/CMS1500/lib/amountToCents';
import Icon from 'components/shared/Icon/Icon';
import Typography from 'components/shared/Typography/Typography';

const AdjustmentCodes = ({ procedure = {} }) => {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['getCodes'],
    queryFn: getCodes,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const codes = ia(data?.codes) ? arrayToObject(data?.codes, 'code', 'description') : [];

  async function getCodes() {
    return requestApi({
      navigate,
      url: '/api/codes/get',
      params: { services: ['adjustment_reason', 'claim_adjustment_reason'] }
    });
  }

  const renderTable = (title, codesArr) => {
    return (
      <div className="mb-4 text-left">
        <Typography size="medium" variant="p" className="mb-2 font-bold">
          {title}
        </Typography>
        <table className="w-full text-left">
          <thead className="bg-primary-25 text-left">
            <tr>
              <th className="px-4 py-2">Amount</th>
              <th className="px-4 py-2">Adjustment Code</th>
            </tr>
          </thead>
          <tbody>
            {codesArr?.map((adj, i) => {
              const adjustments = [...iaRa(adj?.ins_adj), ...iaRa(adj?.pt_adj)];
              return adjustments?.map((a, j) => {
                const { group, code, amount } = a || {};
                return (
                  <tr key={`${i}-${j}`} className="odd:bg-white even:bg-primary-25">
                    <td className="px-4 py-2">{mString(amountToCents(amount))}</td>
                    <td className="px-4 py-2">
                      {group && code ? (
                        <Tippy
                          content={
                            <div>
                              <p className="text-xs">{`${group} - ${codes[group]}`}</p>
                              <p className="text-xs">{`${code} - ${codes[code]}`}</p>
                            </div>
                          }
                          interactive>
                          <div className="relative flex cursor-pointer items-center gap-1">
                            <span className="cursor-pointer">{`${group}${code}`}</span>
                            <Icon icon="info-linear" />
                          </div>
                        </Tippy>
                      ) : (
                        mString(0)
                      )}
                    </td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const hasAdjustments =
    ia(procedure?.primary) || ia(procedure?.secondary) || ia(procedure?.tertiary);

  if (!hasAdjustments) return null;

  const tableContent = (
    <div className="p-2">
      {ia(procedure?.primary) && renderTable('Primary', procedure?.primary)}
      {ia(procedure?.secondary) && renderTable('Secondary', procedure?.secondary)}
      {ia(procedure?.tertiary) && renderTable('Tertiary', procedure?.tertiary)}
    </div>
  );

  return (
    <Tippy className="!w-[400px]" content={tableContent} interactive>
      <div className="relative flex cursor-pointer items-center gap-1">
        <Icon icon="info-linear" />
      </div>
    </Tippy>
  );
};

export default AdjustmentCodes;
